import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, ChefHat, ConciergeBell, Loader2, Plus, ArrowRightLeft, ShieldCheck, AlertTriangle, Building2, UserCog, FileDown, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateTime } from "@/lib/format";

const SHIFT_META = {
  M:     { label: "M",     tone: "bg-sky-100 text-sky-800 border-sky-200" },
  A:     { label: "A",     tone: "bg-orange-100 text-orange-800 border-orange-200" },
  E:     { label: "E",     tone: "bg-amber-100 text-amber-800 border-amber-200" },
  N:     { label: "N",     tone: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  OFF:   { label: "OFF",   tone: "bg-slate-100 text-slate-600 border-slate-200" },
  BREAK: { label: "BRK",   tone: "bg-teal-100 text-teal-800 border-teal-200" },
  LEAVE: { label: "LV",    tone: "bg-rose-100 text-rose-700 border-rose-200" },
  PAID:  { label: "PL",    tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  ABS:   { label: "ABS",   tone: "bg-red-100 text-red-800 border-red-300" },
  TR:    { label: "TR",    tone: "bg-purple-100 text-purple-700 border-purple-200" },
  "":    { label: "—",     tone: "bg-white text-slate-400 border-slate-200" },
};

const WORKING_CODES = new Set(["M", "A", "E", "N", "BREAK"]);

const DEPT_META = {
  kitchen: { label: "Kitchen", icon: ChefHat,        tone: "text-amber-600" },
  service: { label: "Service", icon: ConciergeBell, tone: "text-indigo-600" },
  other:   { label: "Other",   icon: UserCog,        tone: "text-slate-600" },
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dateLabel = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", weekday: "short" });
};

export default function Roster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isHOD = user?.role === "hod";
  const isOpManager = user?.role === "op_manager";
  const isSupervisor = !!user?.is_roster_supervisor;
  const canManageAny = isAdmin || isSupervisor || isHOD || isOpManager;

  const [searchParams, setSearchParams] = useSearchParams();

  // "grid" (supervisor/admin view) or "mine" (regular staff view)
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    if (t === "grid" && canManageAny) return "grid";
    if (t === "mine") return "mine";
    return canManageAny ? "grid" : "mine";
  });

  // Deep-link targets from the notification bell
  const [highlightLeaveId, setHighlightLeaveId] = useState(() => searchParams.get("highlight_leave") || "");
  const [highlightShiftId, setHighlightShiftId] = useState(() => searchParams.get("highlight_shift") || "");
  const highlightScrolledRef = useRef({ leave: null, shift: null });

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [department, setDepartment] = useState(user?.department || "kitchen");
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [nextStatus, setNextStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ from_date: addDays(isoToday(), 2), to_date: addDays(isoToday(), 2), reason: "" });
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [myShifts, setMyShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);

  // Transfer-cell dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferCtx, setTransferCtx] = useState(null); // { staff, date, shift }
  const [transferTo, setTransferTo] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);

  const [permOpen, setPermOpen] = useState(false);

  // Monthly Log dialog state — admin & HOD only.
  const [monthlyLogOpen, setMonthlyLogOpen] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryDays, setSummaryDays] = useState(30);

  // Custom legend chips (admin/HOD-managed, additive to the built-in codes).
  const [customLegend, setCustomLegend] = useState([]);
  const [legendDialogOpen, setLegendDialogOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster/legend");
        setCustomLegend(data);
      } catch { /* ignore */ }
    })();
  }, []);
  const reloadLegend = useCallback(async () => {
    try {
      const { data } = await api.get("/roster/legend");
      setCustomLegend(data);
    } catch { /* ignore */ }
  }, []);

  // Load outlets
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/outlets");
      const os = data.filter((o) => !o.is_internal);
      setOutlets(os);
      // Deep-link: prefer outlet coming from a roster-transfer notification.
      const wantedOutlet = searchParams.get("outlet_id");
      const initial = (wantedOutlet && os.find((o) => o.id === wantedOutlet)?.id) || os[0]?.id || "";
      if (!outletId) setOutletId(initial);
      const wantedDept = searchParams.get("department");
      if (wantedDept && ["kitchen", "service", "other"].includes(wantedDept)) {
        setDepartment(wantedDept);
      }
    })();
  }, []); // eslint-disable-line

  // Load periods when outlet/dept change
  const reloadPeriods = useCallback(async () => {
    if (!outletId) return;
    const { data } = await api.get(`/roster/periods?outlet_id=${outletId}&department=${department}`);
    setPeriods(data);
    // Prefer the period covering the deep-link date (transfer notification),
    // otherwise the current/latest period.
    const wantedDate = searchParams.get("date");
    const today = isoToday();
    const forDate = wantedDate && data.find((p) => p.start_date <= wantedDate && p.end_date >= wantedDate);
    const current = forDate || data.find((p) => p.start_date <= today && p.end_date >= today) || data[0];
    setPeriodId(current?.id || "");
    // Next-status alert
    try {
      const ns = await api.get(`/roster/next-status?outlet_id=${outletId}&department=${department}`);
      setNextStatus(ns.data);
    } catch { setNextStatus(null); }
    // Staff
    try {
      const s = await api.get(`/roster/staff?outlet_id=${outletId}&department=${department}`);
      setStaff(s.data);
    } catch { setStaff([]); }
  }, [outletId, department]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === "grid") reloadPeriods(); }, [tab, reloadPeriods]);

  const loadSummary = useCallback(async () => {
    if (!outletId || !department) return;
    try {
      const { data } = await api.get(`/roster/summary?outlet_id=${outletId}&department=${department}&days=${summaryDays}`);
      setSummary(data);
    } catch { setSummary(null); }
  }, [outletId, department, summaryDays]);

  useEffect(() => {
    if (tab === "grid" && canManageAny) loadSummary();
  }, [tab, canManageAny, loadSummary, shifts]); // reload on shift changes so counts stay fresh

  // Load shifts
  useEffect(() => {
    (async () => {
      if (!periodId) { setShifts([]); return; }
      const { data } = await api.get(`/roster/shifts?period_id=${periodId}`);
      setShifts(data);
    })();
  }, [periodId]);

  // My-view + leaves
  const loadMine = useCallback(async () => {
    const [m, l] = await Promise.all([api.get("/roster/my"), api.get("/leave")]);
    setMyShifts(m.data);
    setLeaves(l.data);
  }, []);
  const loadLeaves = useCallback(async () => {
    try {
      const { data } = await api.get("/leave");
      setLeaves(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (tab === "mine") loadMine(); }, [tab, loadMine]);
  // Supervisors/admins need leaves in the grid tab too so they can approve inline
  useEffect(() => { if (tab === "grid" && (isAdmin || isSupervisor || isHOD || isOpManager)) loadLeaves(); }, [tab, isAdmin, isSupervisor, isHOD, isOpManager, loadLeaves]);

  // Deep-link highlighting: once the target row/cell exists in the DOM, scroll
  // it into view and let the ring animation play, then clear the query params
  // so a refresh doesn't keep highlighting the same item.
  useEffect(() => {
    if (!highlightLeaveId) return;
    if (highlightScrolledRef.current.leave === highlightLeaveId) return;
    const el = document.querySelector(`[data-testid="leave-row-${highlightLeaveId}"]`);
    if (!el) return;
    highlightScrolledRef.current.leave = highlightLeaveId;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => {
      setHighlightLeaveId("");
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete("highlight_leave");
        return p;
      }, { replace: true });
    }, 3500);
    return () => clearTimeout(t);
  }, [highlightLeaveId, leaves, tab, setSearchParams]);

  useEffect(() => {
    if (!highlightShiftId) return;
    if (highlightScrolledRef.current.shift === highlightShiftId) return;
    const el = document.querySelector(`[data-highlight-shift="${highlightShiftId}"]`);
    if (!el) return;
    highlightScrolledRef.current.shift = highlightShiftId;
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    const t = setTimeout(() => {
      setHighlightShiftId("");
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete("highlight_shift");
        p.delete("date");
        return p;
      }, { replace: true });
    }, 3500);
    return () => clearTimeout(t);
  }, [highlightShiftId, shifts, tab, periodId, setSearchParams]);

  const period = useMemo(() => periods.find((p) => p.id === periodId), [periods, periodId]);
  const days = useMemo(() => {
    if (!period) return [];
    const out = [];
    for (let i = 0; i < 10; i++) out.push(addDays(period.start_date, i));
    return out;
  }, [period]);

  const shiftMap = useMemo(() => {
    // key: `${staff_id}|${date}` → shift doc
    const m = {};
    for (const s of shifts) m[`${s.staff_id}|${s.date}`] = s;
    return m;
  }, [shifts]);

  const canManageHere = isAdmin || isHOD || (isSupervisor && user?.department === department && user?.outlet_ids?.includes(outletId));

  const setCell = async (staffMember, date, code) => {
    if (!canManageHere || !periodId) return;
    setBusy(true);
    try {
      const { data } = await api.put("/roster/shifts", { period_id: periodId, staff_id: staffMember.id, date, shift_code: code });
      setShifts((prev) => {
        const idx = prev.findIndex((x) => x.staff_id === data.staff_id && x.date === data.date);
        if (idx === -1) return [...prev, data];
        const next = prev.slice();
        next[idx] = data;
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const createNextPeriod = async () => {
    if (!nextStatus?.suggested_next_start) return;
    setBusy(true);
    try {
      await api.post("/roster/periods", { outlet_id: outletId, department, start_date: nextStatus.suggested_next_start });
      toast.success("Next 10-day roster created");
      await reloadPeriods();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    setLeaveSaving(true);
    try {
      await api.post("/leave", leaveForm);
      toast.success("Leave request submitted");
      setLeaveOpen(false);
      loadMine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setLeaveSaving(false); }
  };

  const decideLeave = async (id, status) => {
    try {
      await api.patch(`/leave/${id}`, { status });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      loadLeaves();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openTransferForCell = (staffMember, date, shiftDoc) => {
    if (!shiftDoc?.id) {
      toast.info("Assign a shift before transferring — the transfer needs a saved cell to move.");
      return;
    }
    setTransferCtx({ staff: staffMember, date, shift: shiftDoc });
    setTransferTo("");
    setTransferReason("");
    setTransferOpen(true);
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferCtx?.shift?.id) return;
    if (!transferTo) return toast.error("Pick a destination outlet");
    setTransferBusy(true);
    try {
      await api.post("/roster/transfers", {
        shift_id: transferCtx.shift.id,
        to_outlet_id: transferTo,
        reason: transferReason,
      });
      const destName = outlets.find((o) => o.id === transferTo)?.code || "destination";
      toast.success(`Transferred ${transferCtx.staff.name}'s ${transferCtx.date} shift → ${destName}`);
      setTransferOpen(false);
      // Refresh the current period's cells so the source shows TR immediately
      const { data } = await api.get(`/roster/shifts?period_id=${periodId}`);
      setShifts(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally { setTransferBusy(false); }
  };

  const outlet = outlets.find((o) => o.id === outletId);

  const exportPDF = useCallback(() => {
    if (!period || staff.length === 0) {
      toast.error("Nothing to export yet");
      return;
    }
    // Landscape A4 fits 10 date columns + staff + total comfortably.
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const outletLabel = outlet ? `${outlet.code} · ${outlet.name}` : outletId;
    const deptLabel = DEPT_META[department]?.label || department;
    const title = `Duty Roster — ${outletLabel}`;
    const subtitle = `${deptLabel} · ${period.start_date} → ${period.end_date}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, 40, 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90);
    doc.text(subtitle, 40, 52);
    doc.setTextColor(20);

    const head = [["Staff", ...days.map(dateLabel), "Days"]];
    const body = staff.map((s) => {
      let worked = 0;
      const row = [
        { content: `${s.name}\n${s.role}`, styles: { fontStyle: "bold" } },
        ...days.map((d) => {
          const code = shiftMap[`${s.id}|${d}`]?.shift_code || "";
          if (WORKING_CODES.has(code)) worked += 1;
          return SHIFT_META[code]?.label || "—";
        }),
        `${worked}/10`,
      ];
      return row;
    });

    autoTable(doc, {
      head,
      body,
      startY: 68,
      styles: { fontSize: 9, halign: "center", cellPadding: 4, lineColor: [220, 220, 230], lineWidth: 0.4 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, halign: "center", fontStyle: "bold" },
      columnStyles: { 0: { halign: "left", cellWidth: 110, fontSize: 8 } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index > 0 && data.column.index <= days.length) {
          const label = String(data.cell.raw ?? "");
          const tint = {
            M: [224, 242, 254], A: [255, 237, 213], E: [254, 243, 199], N: [224, 231, 255],
            BRK: [204, 251, 241], OFF: [241, 245, 249], LV: [254, 226, 226], PL: [209, 250, 229],
            ABS: [254, 202, 202], TR: [233, 213, 255],
          }[label];
          if (tint) {
            data.cell.styles.fillColor = tint;
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
    });

    const footY = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(8);
    doc.setTextColor(120);
    const legend = "M Morning · A Afternoon · E Evening · N Night · BRK Break · OFF · LV Leave · PL Paid Leave · ABS Absent · TR Transfer";
    doc.text(legend, 40, footY);
    doc.text(`Generated ${new Date().toLocaleString()} · Job Ticker`, 40, footY + 12);

    const fname = `Roster_${outlet?.code || "outlet"}_${deptLabel}_${period.start_date}.pdf`.replace(/\s+/g, "");
    doc.save(fname);
    toast.success("Roster PDF downloaded");
  }, [period, staff, days, shiftMap, outlet, outletId, department]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Duty Roster</h1>
          <p className="text-sm text-slate-500 mt-1">
            10-day schedules per outlet & department. Apply for leave at least 2 days ahead.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="roster-apply-leave" onClick={() => setLeaveOpen(true)} variant="outline" className="h-11">
            <CalendarDays className="w-4 h-4 mr-1.5" /> Apply for leave
          </Button>
          {(isAdmin || isSupervisor || isHOD) && (
            <Button data-testid="roster-open-permissions" onClick={() => setPermOpen(true)} variant="outline" className="h-11">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Roster roles
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5">
        {canManageAny && (
          <button data-testid="roster-tab-grid" onClick={() => setTab("grid")}
            className={`h-9 px-3 rounded-full text-xs font-semibold border ${tab === "grid" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>
            Roster grid
          </button>
        )}
        <button data-testid="roster-tab-mine" onClick={() => setTab("mine")}
          className={`h-9 px-3 rounded-full text-xs font-semibold border ${tab === "mine" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>
          My duties
        </button>
      </div>

      {tab === "grid" && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger data-testid="roster-outlet" className="h-10 w-64"><SelectValue placeholder="Outlet" /></SelectTrigger>
                <SelectContent>
                  {outlets.map((o) => (<SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              {Object.entries(DEPT_META).map(([k, m]) => {
                const Icon = m.icon;
                return (
                  <button
                    key={k}
                    data-testid={`roster-dept-${k}`}
                    onClick={() => setDepartment(k)}
                    className={`h-10 px-3 rounded-lg text-xs font-semibold border inline-flex items-center gap-1.5 ${department === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {m.label}
                  </button>
                );
              })}
            </div>
            {periods.length > 0 && (
              <Select value={periodId} onValueChange={setPeriodId}>
                <SelectTrigger data-testid="roster-period" className="h-10 w-64"><SelectValue placeholder="Period" /></SelectTrigger>
                <SelectContent>
                  {periods.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.start_date} → {p.end_date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {period && staff.length > 0 && (
              <Button
                data-testid="roster-export-pdf"
                onClick={exportPDF}
                variant="outline"
                className="h-10"
                title="Download this 10-day grid as a PDF"
              >
                <FileDown className="w-4 h-4 mr-1.5" /> Export PDF
              </Button>
            )}
            {(isAdmin || isHOD) && (
              <Button
                data-testid="roster-monthly-log-btn"
                onClick={() => setMonthlyLogOpen(true)}
                variant="outline"
                className="h-10"
                title="Download the full monthly roster log (CSV / PDF)"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Monthly Log
              </Button>
            )}
          </div>

          {/* Create next period — available ANY TIME a next period doesn't
              exist yet, not just inside the 48h overdue window. Overdue
              banner (below) is still the louder cue when the deadline is
              near. */}
          {canManageHere && period && nextStatus && !nextStatus.next_exists && !nextStatus.overdue && (
            <div data-testid="roster-create-next-cta" className="rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-900 p-3 flex items-center gap-3">
              <CalendarDays className="w-4 h-4 text-indigo-600" />
              <div className="flex-1 text-xs">
                Next 10-day period isn't published yet — starts <span className="font-semibold">{nextStatus.suggested_next_start}</span>.
              </div>
              <Button
                data-testid="roster-create-next-early"
                onClick={createNextPeriod}
                disabled={busy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create next period"}
              </Button>
            </div>
          )}

          {/* Overdue banner */}
          {nextStatus?.overdue && canManageHere && (
            <div data-testid="roster-overdue-banner" className="rounded-xl border border-rose-200 bg-rose-50 text-rose-900 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-sm">Next roster overdue</div>
                <div className="text-xs mt-0.5">
                  The current 10-day period ends in {nextStatus.hours_to_end}h at {outlet?.code} · {DEPT_META[department].label}. No next period exists yet — publish it before the deadline.
                </div>
              </div>
              <Button data-testid="roster-create-next" onClick={createNextPeriod} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create next 10 days"}
              </Button>
            </div>
          )}

          {/* No period yet */}
          {!period && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <CalendarDays className="w-8 h-8 text-slate-400 mx-auto" />
              <div className="mt-3 font-semibold text-slate-800">No roster yet</div>
              <div className="text-sm text-slate-500 mb-4">Publish the first 10-day period for {outlet?.code} · {DEPT_META[department].label}.</div>
              {canManageHere && (
                <Button data-testid="roster-create-first" onClick={() => api.post("/roster/periods", { outlet_id: outletId, department, start_date: nextStatus?.suggested_next_start || isoToday() }).then(() => { toast.success("Period created"); reloadPeriods(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"))} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  <Plus className="w-4 h-4 mr-1.5" /> Create first period
                </Button>
              )}
            </div>
          )}

          {/* Grid */}
          {period && (
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-3 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[180px]">Staff</th>
                      {days.map((d) => (
                        <th key={d} className="px-2 py-3 text-center font-semibold whitespace-nowrap">
                          <div>{dateLabel(d)}</div>
                        </th>
                      ))}
                      <th className="px-2 py-3 text-center font-semibold whitespace-nowrap" data-testid="roster-col-days">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {staff.length === 0 && (
                      <tr><td colSpan={12} className="px-4 py-8 text-center text-slate-500 text-sm">
                        No {DEPT_META[department].label} staff at {outlet?.code}. Assign a department to users first via <span className="font-semibold">Roster roles</span>.
                      </td></tr>
                    )}
                    {staff.map((s) => {
                      const worked = days.reduce((acc, d) => {
                        const code = shiftMap[`${s.id}|${d}`]?.shift_code || "";
                        return WORKING_CODES.has(code) ? acc + 1 : acc;
                      }, 0);
                      return (
                      <tr key={s.id} data-testid={`roster-row-${s.id}`} className="hover:bg-slate-50/50">
                        <td className="px-3 py-2 sticky left-0 bg-white z-10">
                          <div className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                            {s.name}
                          </div>
                          <div className="text-[10px] text-slate-500">{s.role}</div>
                        </td>
                        {days.map((d) => {
                          const key = `${s.id}|${d}`;
                          const cell = shiftMap[key];
                          const code = cell?.shift_code || "";
                          const meta = SHIFT_META[code] || SHIFT_META[""];
                          const isTransferred = code === "TR";
                          const isHighlighted = highlightShiftId && cell?.id === highlightShiftId;
                          const canEditThisRow = canManageHere;
                          return (
                            <td
                              key={d}
                              className={`px-1 py-1.5 text-center ${isHighlighted ? "roster-highlight-cell" : ""}`}
                              data-highlight-shift={cell?.id || undefined}
                            >
                              {canEditThisRow && !isTransferred ? (
                                <Select value={code || "_"} onValueChange={(v) => {
                                  if (v === "__transfer__") { openTransferForCell(s, d, cell); return; }
                                  setCell(s, d, v === "_" ? "" : v);
                                }}>
                                  <SelectTrigger
                                    data-testid={`roster-cell-${s.id}-${d}`}
                                    className={`h-8 w-16 border ${meta.tone} font-bold text-xs justify-center`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_">—</SelectItem>
                                    <SelectItem value="M">M · Morning</SelectItem>
                                    <SelectItem value="A">A · Afternoon</SelectItem>
                                    <SelectItem value="E">E · Evening</SelectItem>
                                    <SelectItem value="N">N · Night</SelectItem>
                                    <SelectItem value="BREAK">BRK · Break shift</SelectItem>
                                    <SelectItem value="OFF">OFF</SelectItem>
                                    <SelectItem value="LEAVE">LEAVE</SelectItem>
                                    <SelectItem value="PAID" data-testid={`roster-mark-paid-${s.id}-${d}`}>PL · Paid leave</SelectItem>
                                    <SelectItem value="ABS" data-testid={`roster-mark-absent-${s.id}-${d}`}>ABS · Absent (no-show)</SelectItem>
                                    {cell?.id && cell.shift_code && cell.shift_code !== "TR" && outlets.length > 1 && (
                                      <SelectItem value="__transfer__" data-testid={`roster-transfer-opt-${s.id}-${d}`}>
                                        Transfer to another outlet…
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span
                                  data-testid={`roster-cell-ro-${s.id}-${d}`}
                                  title={isTransferred && cell?.note ? cell.note : ""}
                                  className={`inline-flex items-center justify-center h-8 w-16 rounded-md border text-xs font-bold ${meta.tone}`}
                                >
                                  {meta.label}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-center">
                          <span
                            data-testid={`roster-days-${s.id}`}
                            className="inline-flex items-center justify-center h-7 min-w-[2.5rem] rounded-md bg-slate-900 text-white text-xs font-bold tabular-nums"
                            title={`${worked} working day${worked === 1 ? "" : "s"} in this 10-day period`}
                          >
                            {worked}/10
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t border-slate-100 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <span className="font-semibold">Legend:</span>
                {["M","A","E","N","BREAK","OFF","LEAVE","PAID","ABS","TR"].map((c) => (
                  <span key={c} className={`px-2 py-0.5 rounded border ${SHIFT_META[c].tone} font-bold`}>{SHIFT_META[c].label}</span>
                ))}
                {customLegend.map((l) => (
                  <span
                    key={l.id}
                    data-testid={`roster-legend-chip-${l.id}`}
                    title={l.description}
                    className={`px-2 py-0.5 rounded border ${l.tone} font-bold`}
                  >
                    {l.label}
                  </span>
                ))}
                {(isAdmin || isHOD) && (
                  <button
                    data-testid="roster-legend-manage"
                    onClick={() => setLegendDialogOpen(true)}
                    className="ml-auto text-[11px] font-semibold text-indigo-700 hover:underline"
                  >
                    Manage legend
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Attendance summary (admin + supervisor) */}
          {canManageAny && summary && summary.rows && summary.rows.length > 0 && (
            <div data-testid="roster-summary" className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="font-semibold text-slate-900 text-sm">Attendance summary</div>
                  <div className="text-[11px] text-slate-500">{summary.start_date} → {summary.end_date} · {outlet?.code} · {DEPT_META[department].label}</div>
                </div>
                <Select value={String(summaryDays)} onValueChange={(v) => setSummaryDays(parseInt(v, 10))}>
                  <SelectTrigger data-testid="roster-summary-range" className="h-9 w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">Last 10 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Staff</th>
                      <th className="text-right px-2 py-2 font-semibold">Working</th>
                      <th className="text-right px-2 py-2 font-semibold">Off</th>
                      <th className="text-right px-2 py-2 font-semibold">Leave</th>
                      <th className="text-right px-2 py-2 font-semibold">Paid leave</th>
                      <th className="text-right px-2 py-2 font-semibold">Absent</th>
                      <th className="text-right px-2 py-2 font-semibold">Transferred</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {summary.rows.map((r) => (
                      <tr key={r.staff_id} data-testid={`roster-summary-row-${r.staff_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900">{r.staff_name}</div>
                          <div className="text-[10px] text-slate-500">{r.role}</div>
                        </td>
                        <td className="px-2 py-2 text-right font-bold text-emerald-700 tabular-nums" data-testid={`roster-summary-working-${r.staff_id}`}>{r.working}</td>
                        <td className="px-2 py-2 text-right text-slate-600 tabular-nums">{r.off}</td>
                        <td className="px-2 py-2 text-right text-rose-700 tabular-nums">{r.leave}</td>
                        <td className="px-2 py-2 text-right text-emerald-700 font-semibold tabular-nums" data-testid={`roster-summary-paid-${r.staff_id}`}>{r.paid_leave || 0}</td>
                        <td className="px-2 py-2 text-right text-red-700 font-semibold tabular-nums" data-testid={`roster-summary-absent-${r.staff_id}`}>{r.absent}</td>
                        <td className="px-2 py-2 text-right text-purple-700 tabular-nums">{r.transferred}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pending leaves (for supervisors/admins/HOD) */}
          {(isAdmin || isSupervisor || isHOD || isOpManager) && (
            <LeavesPanel data={leaves} onDecide={decideLeave} onRefresh={loadMine} canDecide highlightId={highlightLeaveId} />
          )}
        </>
      )}

      {tab === "mine" && (
        <MyDutiesPanel shifts={myShifts} leaves={leaves} highlightLeaveId={highlightLeaveId} />
      )}

      {/* Leave dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
          <form onSubmit={submitLeave} className="space-y-3">
            <div className="rounded-lg bg-amber-50 border border-amber-100 text-amber-900 text-xs px-3 py-2">
              Leave must be applied at least 2 days before the from-date.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Input data-testid="leave-from" type="date" min={addDays(isoToday(), 2)} required value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value, to_date: e.target.value > leaveForm.to_date ? e.target.value : leaveForm.to_date })} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>To</Label>
                <Input data-testid="leave-to" type="date" min={leaveForm.from_date} required value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} className="mt-1.5 h-11" />
              </div>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea data-testid="leave-reason" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={leaveSaving} data-testid="leave-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {leaveSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Roster permissions dialog (admin) */}
      {(isAdmin || isSupervisor || isHOD) && <PermissionsDialog open={permOpen} onOpenChange={setPermOpen} outlets={outlets} isAdmin={isAdmin} isHOD={isHOD} currentUser={user} />}

      {/* Transfer cell dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transfer duty to another outlet</DialogTitle></DialogHeader>
          {transferCtx && (
            <form onSubmit={submitTransfer} className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600 space-y-0.5">
                <div><span className="text-slate-500">Staff:</span> <span className="font-semibold text-slate-900">{transferCtx.staff.name}</span></div>
                <div><span className="text-slate-500">Date:</span> <span className="font-semibold text-slate-900">{dateLabel(transferCtx.date)}</span></div>
                <div><span className="text-slate-500">Current shift:</span> <span className="font-semibold text-slate-900">{transferCtx.shift?.shift_code}</span> at {outlet?.code}</div>
              </div>
              <div>
                <Label>Destination outlet</Label>
                <Select value={transferTo} onValueChange={setTransferTo}>
                  <SelectTrigger data-testid="roster-transfer-to" className="mt-1.5 h-11"><SelectValue placeholder="Pick outlet" /></SelectTrigger>
                  <SelectContent>
                    {outlets.filter((o) => o.id !== outletId).map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea data-testid="roster-transfer-reason" value={transferReason} onChange={(e) => setTransferReason(e.target.value)} className="mt-1.5" placeholder="e.g. covering leave at Koramangala" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={transferBusy || !transferTo} data-testid="roster-transfer-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {transferBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ArrowRightLeft className="w-4 h-4 mr-1.5" /> Transfer</>)}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Legend manager (admin/HOD only) */}
      <LegendManagerDialog
        open={legendDialogOpen}
        onOpenChange={setLegendDialogOpen}
        legend={customLegend}
        onChanged={reloadLegend}
      />
    </div>
  );
}

function MyDutiesPanel({ shifts, leaves, highlightLeaveId }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="font-semibold text-slate-900 text-sm mb-3">Upcoming duties (next 20 days)</div>
        {shifts.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 text-center">No duties published yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {shifts.map((s) => {
              const meta = SHIFT_META[s.shift_code] || SHIFT_META[""];
              return (
                <li key={s.id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <span className={`h-8 w-14 rounded-md border font-bold text-xs inline-flex items-center justify-center ${meta.tone}`}>{meta.label}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">{dateLabel(s.date)}</div>
                    <div className="text-[11px] text-slate-500">{s.outlet_code} · {s.outlet_name}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <LeavesPanel data={leaves} highlightId={highlightLeaveId} />
    </div>
  );
}

function LeavesPanel({ data, onDecide, canDecide, highlightId }) {
  const pending = (data || []).filter((x) => x.status === "pending");
  const others = (data || []).filter((x) => x.status !== "pending");
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="font-semibold text-slate-900 text-sm mb-3">Leave requests</div>
      {(!data || data.length === 0) && <div className="text-xs text-slate-500 py-4 text-center">No requests yet.</div>}
      {pending.length > 0 && (
        <>
          <div className="text-[10px] font-semibold uppercase text-amber-700 mb-1.5">Pending</div>
          <ul className="space-y-2 mb-3">
            {pending.map((l) => (
              <li key={l.id} data-testid={`leave-row-${l.id}`} className={`rounded-lg border border-amber-100 bg-amber-50 p-3 ${highlightId === l.id ? "roster-highlight-row" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{l.staff_name}</div>
                    <div className="text-[11px] text-slate-600">{l.from_date} → {l.to_date} · {l.department}</div>
                    {l.reason && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{l.reason}</div>}
                  </div>
                  {canDecide && onDecide && (
                    <div className="flex gap-1.5 shrink-0">
                      <button data-testid={`leave-approve-${l.id}`} onClick={() => onDecide(l.id, "approved")} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">Approve</button>
                      <button data-testid={`leave-reject-${l.id}`} onClick={() => onDecide(l.id, "rejected")} className="text-[10px] font-semibold px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700">Reject</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {others.length > 0 && (
        <>
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1.5">Decided</div>
          <ul className="space-y-1.5">
            {others.slice(0, 8).map((l) => (
              <li key={l.id} data-testid={`leave-row-${l.id}`} className={`text-[11px] py-1.5 border-b border-slate-100 last:border-0 ${highlightId === l.id ? "roster-highlight-row" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-700 truncate">{l.staff_name} · {l.from_date} → {l.to_date}</span>
                  <span className={`px-1.5 py-0.5 rounded font-semibold shrink-0 ${l.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{l.status}</span>
                </div>
                {(l.decided_by_name || l.decided_at) && (
                  <div data-testid={`leave-decision-${l.id}`} className="text-[10px] text-slate-500 mt-0.5">
                    {l.status === "approved" ? "Approved" : "Rejected"} by <span className="font-semibold text-slate-700">{l.decided_by_name || "—"}</span>
                    {l.decided_at ? ` · ${formatDateTime(l.decided_at)}` : ""}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function PermissionsDialog({ open, onOpenChange, outlets = [], isAdmin, isHOD, currentUser }) {
  const [users, setUsers] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [allotFor, setAllotFor] = useState(null); // user being allotted outlets
  const [allotIds, setAllotIds] = useState([]);
  const [allotSaving, setAllotSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const update = async (u, patch) => {
    setSavingId(u.id);
    try {
      const { data } = await api.put(`/users/${u.id}`, patch);
      setUsers((prev) => prev.map((x) => x.id === u.id ? data : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSavingId(null); }
  };

  // Only admins can flip department + supervisor bits. HODs allot outlets to
  // supervisors in their dept. Seniors (supervisors) allot outlets to
  // outlet_staff in their dept.
  const canAllot = (u) => {
    if (!u) return false;
    if (isAdmin) return ["outlet_staff", "facility_incharge", "outlet_incharge", "hod"].includes(u.role);
    if (isHOD) {
      // HOD allots outlets to anyone in their department (staff + supervisors),
      // but never touches HOD-level assignments (that's admin's job).
      if (u.role === "hod") return false;
      return (u.department || "other") === (currentUser?.department || "other");
    }
    if (!["outlet_staff", "facility_incharge", "outlet_incharge"].includes(u.role)) return false;
    if (u.role !== "outlet_staff") return false;
    return (u.department || "other") === (currentUser?.department || "other");
  };

  // Outlets the requester can choose from. Admin/HOD see all outlets; regular
  // supervisors see only their own outlets.
  const availableOutlets = (isAdmin || isHOD)
    ? outlets
    : outlets.filter((o) => (currentUser?.outlet_ids || []).includes(o.id));

  const openAllot = (u) => {
    setAllotFor(u);
    setAllotIds(u.outlet_ids || []);
  };

  const saveAllot = async () => {
    if (!allotFor) return;
    setAllotSaving(true);
    try {
      await api.post(`/users/${allotFor.id}/assign-outlets`, { outlet_ids: allotIds });
      toast.success(`Outlets updated for ${allotFor.name}`);
      setAllotFor(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setAllotSaving(false); }
  };

  const outletCode = (id) => outlets.find((o) => o.id === id)?.code || id.slice(0, 6);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Roster roles & outlets</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 mb-2">
          {isAdmin
            ? "Assign a department, tick HOD to promote someone to Head of Department, toggle Supervisor for department seniors, and allot outlets to Outlet Staff."
            : isHOD
              ? "Allot outlets to the Supervisors in your department. Everything else is admin-only."
              : "Allot outlets to Outlet Staff in your department. Departments and Supervisor toggles are admin-only."}
        </div>
        <div className="overflow-y-auto flex-1 space-y-1 -mx-2 px-2">
          {users === null && <div className="text-center py-6"><Loader2 className="w-4 h-4 mx-auto animate-spin text-slate-400" /></div>}
          {users && users.map((u) => (
            <div key={u.id} data-testid={`perm-row-${u.id}`} className="flex items-center gap-3 rounded-lg border border-slate-100 p-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                <div className="text-[10px] text-slate-500">{u.role} · {u.email}</div>
                {u.outlet_ids?.length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                    Outlets: <span className="font-semibold text-slate-700">{u.outlet_ids.map(outletCode).join(", ")}</span>
                  </div>
                )}
              </div>
              <Select value={u.department || "other"} disabled={!isAdmin} onValueChange={(v) => update(u, { department: v })}>
                <SelectTrigger data-testid={`perm-dept-${u.id}`} className="h-9 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer" title="Head of Department — sits above supervisors">
                  <input
                    type="checkbox"
                    data-testid={`perm-hod-${u.id}`}
                    checked={u.role === "hod"}
                    disabled={savingId === u.id}
                    onChange={(e) => update(u, { role: e.target.checked ? "hod" : "facility_incharge" })}
                  />
                  <span className="text-[11px] font-semibold text-slate-700">HOD</span>
                </label>
              )}
              {isAdmin && (
                <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`perm-supervisor-${u.id}`}
                    checked={!!u.is_roster_supervisor}
                    onChange={(e) => update(u, { is_roster_supervisor: e.target.checked })}
                    disabled={savingId === u.id}
                  />
                  <span className="text-[11px] font-semibold text-slate-700">Supervisor</span>
                </label>
              )}
              {canAllot(u) && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`perm-allot-outlets-${u.id}`}
                  onClick={() => openAllot(u)}
                  className="h-8 shrink-0"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1" /> Allot
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>

    {/* Per-user outlet allotment dialog */}
    <Dialog open={!!allotFor} onOpenChange={(v) => !v && setAllotFor(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Allot outlets — {allotFor?.name}</DialogTitle></DialogHeader>
        {allotFor && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Pick every outlet <span className="font-semibold text-slate-800">{allotFor.name}</span> ({allotFor.role}) is allowed to work at.
              {!(isAdmin || isHOD) && <> Only your allotted outlets show here.</>}
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {availableOutlets.length === 0 && (
                <div className="text-xs text-slate-500 py-4 text-center">No outlets available to allot.</div>
              )}
              {availableOutlets.map((o) => (
                <label key={o.id} className="flex items-center gap-2 rounded-md border border-slate-100 p-2 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    data-testid={`perm-allot-check-${o.id}`}
                    checked={allotIds.includes(o.id)}
                    onChange={(e) => setAllotIds(e.target.checked ? [...allotIds, o.id] : allotIds.filter((x) => x !== o.id))}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">{o.code}</div>
                    <div className="text-[11px] text-slate-500 truncate">{o.name}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAllotFor(null)}>Cancel</Button>
              <Button data-testid="perm-allot-save" disabled={allotSaving} onClick={saveAllot} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {allotSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save outlets"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}


const LEGEND_TONES = [
  { key: "slate",   value: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "sky",     value: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "indigo",  value: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { key: "emerald", value: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "amber",   value: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "orange",  value: "bg-orange-100 text-orange-800 border-orange-200" },
  { key: "rose",    value: "bg-rose-100 text-rose-700 border-rose-200" },
  { key: "purple",  value: "bg-purple-100 text-purple-700 border-purple-200" },
  { key: "teal",    value: "bg-teal-100 text-teal-800 border-teal-200" },
];

function LegendManagerDialog({ open, onOpenChange, legend, onChanged }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [tone, setTone] = useState(LEGEND_TONES[0].value);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const resetForm = () => { setLabel(""); setDescription(""); setTone(LEGEND_TONES[0].value); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !description.trim()) { toast.error("Label and description are required"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/roster/legend/${editingId}`, { label: label.trim(), description: description.trim(), tone });
        toast.success("Legend updated");
      } else {
        await api.post("/roster/legend", { label: label.trim(), description: description.trim(), tone });
        toast.success("Legend added");
      }
      resetForm();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const startEdit = (l) => { setEditingId(l.id); setLabel(l.label); setDescription(l.description); setTone(l.tone); };
  const remove = async (l) => {
    if (!confirm(`Delete legend entry "${l.label} — ${l.description}"?`)) return;
    try { await api.delete(`/roster/legend/${l.id}`); toast.success("Legend removed"); onChanged?.(); if (editingId === l.id) resetForm(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Manage roster legend</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-[11px] text-slate-500">
            Custom legend chips help everyone read the grid — they don't change what shift codes are allowed.
          </div>

          <div className="rounded-lg border border-slate-100 divide-y divide-slate-100 max-h-52 overflow-y-auto">
            {(legend || []).length === 0 && (
              <div className="text-xs text-slate-500 text-center py-4">No custom legend yet.</div>
            )}
            {(legend || []).map((l) => (
              <div key={l.id} data-testid={`legend-row-${l.id}`} className="flex items-center gap-2 px-2 py-1.5">
                <span className={`px-2 py-0.5 rounded border font-bold text-[11px] ${l.tone}`}>{l.label}</span>
                <span className="text-xs text-slate-700 flex-1 truncate">{l.description}</span>
                <button data-testid={`legend-edit-${l.id}`} onClick={() => startEdit(l)} className="text-[11px] text-indigo-700 hover:underline">Edit</button>
                <button data-testid={`legend-delete-${l.id}`} onClick={() => remove(l)} className="text-[11px] text-rose-600 hover:underline">Delete</button>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Label</Label>
                <Input data-testid="legend-form-label" required maxLength={8} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. H" className="mt-1.5 h-10" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input data-testid="legend-form-desc" required maxLength={60} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Half day" className="mt-1.5 h-10" />
              </div>
            </div>
            <div>
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {LEGEND_TONES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`legend-tone-${t.key}`}
                    onClick={() => setTone(t.value)}
                    className={`px-2 py-1 rounded border font-bold text-[11px] ${t.value} ${tone === t.value ? "ring-2 ring-slate-900" : ""}`}
                  >
                    {label.trim() || "Aa"}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>Cancel edit</Button>
              )}


              <Button type="submit" data-testid="legend-form-submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Save changes" : "Add legend")}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const _MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
function _defaultMonthISO() {
  // Default to last month per spec.
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function _monthLabel(iso) {
  const [y, m] = iso.split("-");
  return `${_MONTH_NAMES[parseInt(m, 10) - 1]}${y}`;
}

function MonthlyLogDialog({ open, onOpenChange, isAdmin, isHOD, hodDepartment, hodOutletIds, outlets, defaultOutletId, defaultDepartment }) {
  const visibleOutlets = useMemo(() => {
    const all = (outlets || []).filter((o) => !o.is_internal);
    if (isAdmin) return all;
    // HOD scope: only their allotted outlets.
    const allowed = new Set(hodOutletIds || []);
    return all.filter((o) => allowed.has(o.id));
  }, [outlets, isAdmin, hodOutletIds]);

  const defaultDept = isHOD ? (hodDepartment || "other") : (defaultDepartment || "kitchen");
  const [outletId, setOutletId] = useState(defaultOutletId || visibleOutlets[0]?.id || "");
  const [dept, setDept] = useState(defaultDept);
  const [month, setMonth] = useState(_defaultMonthISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setOutletId(defaultOutletId || visibleOutlets[0]?.id || "");
      setDept(defaultDept);
      setMonth(_defaultMonthISO());
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadPreview = async () => {
    if (!outletId) return toast.error("Pick an outlet");
    if (!dept) return toast.error("Pick a department");
    if (!/^\d{4}-\d{2}$/.test(month)) return toast.error("Month must be YYYY-MM");
    setLoading(true);
    try {
      const { data: d } = await api.get(`/roster/monthly-log?outlet_id=${outletId}&department=${dept}&month=${month}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
      setData(null);
    } finally { setLoading(false); }
  };

  const filename = (ext) => {
    if (!data) return `monthly-log.${ext}`;
    const deptCap = (data.department || "").charAt(0).toUpperCase() + (data.department || "").slice(1);
    return `${data.outlet_code}-${deptCap}-${_monthLabel(data.month)}.${ext}`;
  };

  const downloadCSV = () => {
    if (!data || !data.rows) return;
    const header = ["Date", "Staff Name", "Role", "Department", "Shift Code", "Shift", "Note", "Last Updated By", "Last Updated At"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")].concat(
      data.rows.map((r) => [r.date, r.staff_name, r.staff_role, r.department, r.shift_code || "", r.shift_label, r.note || "", r.updated_by_name || "", r.updated_at || ""].map(esc).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename("csv"); a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    if (!data || !data.rows) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text(`Roster Monthly Log — ${data.outlet_code} · ${data.outlet_name}`, 40, 36);
    doc.setFontSize(10);
    const deptCap = (data.department || "").charAt(0).toUpperCase() + (data.department || "").slice(1);
    doc.text(`Department: ${deptCap}   ·   Month: ${_monthLabel(data.month)}   ·   Entries: ${data.count}`, 40, 54);
    autoTable(doc, {
      startY: 70,
      head: [["Date", "Staff", "Role", "Shift", "Note"]],
      body: data.rows.map((r) => [r.date, r.staff_name, r.staff_role, `${r.shift_code || "—"}${r.shift_label ? " · " + r.shift_label : ""}`, r.note || ""]),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(`Generated ${new Date().toLocaleString()}`, 40, doc.internal.pageSize.getHeight() - 20);
    doc.save(filename("pdf"));
  };

  const previewRows = (data?.rows || []).slice(0, 15);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Download monthly roster log</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500">
            {isHOD ? "You can download your own department's log for any of your allotted outlets." : "Admin can download any outlet × department."}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Outlet</Label>
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger data-testid="monthly-log-outlet" className="mt-1 h-10"><SelectValue placeholder="Outlet" /></SelectTrigger>
                <SelectContent>
                  {visibleOutlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Department</Label>
              <Select value={dept} onValueChange={setDept} disabled={isHOD}>
                <SelectTrigger data-testid="monthly-log-dept" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {isHOD && <div className="text-[10px] text-slate-500 mt-1">Locked to your department.</div>}
            </div>
            <div>
              <Label className="text-xs">Month</Label>
              <Input
                data-testid="monthly-log-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="monthly-log-load" onClick={loadPreview} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load preview"}
            </Button>
            {data && (
              <>
                <Button data-testid="monthly-log-csv" onClick={downloadCSV} variant="outline" className="h-9" disabled={!data.rows || data.rows.length === 0}>
                  <FileDown className="w-4 h-4 mr-1.5" /> Download CSV
                </Button>
                <Button data-testid="monthly-log-pdf" onClick={downloadPDF} variant="outline" className="h-9" disabled={!data.rows || data.rows.length === 0}>
                  <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <span className="ml-auto text-[11px] text-slate-500">
                  {data.count} entr{data.count === 1 ? "y" : "ies"} · {data.month_start} → {data.month_end}
                </span>
              </>
            )}
          </div>
          {data && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Date</th>
                      <th className="text-left px-3 py-2 font-semibold">Staff</th>
                      <th className="text-left px-3 py-2 font-semibold">Role</th>
                      <th className="text-left px-3 py-2 font-semibold">Shift</th>
                      <th className="text-left px-3 py-2 font-semibold">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {previewRows.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No entries for this month.</td></tr>
                    )}
                    {previewRows.map((r, i) => (
                      <tr key={`${r.date}-${r.staff_id}-${i}`} data-testid={`monthly-log-row-${i}`}>
                        <td className="px-3 py-1.5 tabular-nums text-slate-700">{r.date}</td>
                        <td className="px-3 py-1.5 font-semibold text-slate-800">{r.staff_name}</td>
                        <td className="px-3 py-1.5 text-slate-500">{r.staff_role}</td>
                        <td className="px-3 py-1.5">
                          <span className="font-semibold text-slate-900">{r.shift_code || "—"}</span>
                          <span className="text-slate-500"> · {r.shift_label}</span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 truncate">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.rows.length > previewRows.length && (
                <div className="px-3 py-1.5 text-[10px] text-slate-500 bg-slate-50 border-t border-slate-200">
                  Showing first {previewRows.length} of {data.rows.length} entries — download the full file for the rest.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
